/**
 * The client half of an AI recovery-triage job: starting one, holding
 * on to what the POST said, and polling until it finishes.
 *
 * A sibling of `AnalysisResultView.tsx` rather than part of it purely
 * because a file that exports components may export nothing else
 * (`react-refresh/only-export-components`, a hard gate here). The two
 * are one feature and are used together by both panes; the split is
 * behaviour vs. rendering, and nothing else lives on either side of it.
 *
 * Everything here is shared for the same non-forking reason the
 * rendering is: the poll cadence, and the rule that a job which
 * arrives already finished is never polled, are decisions two panes
 * must not hold slightly different copies of -- that is exactly how a
 * cached result silently loses its `cached` flag in one tab and not
 * the other.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createSupportAnalysis,
  getSupportAnalysis,
  type SupportAnalysisResponse,
} from '../api/supportClient';

const RUNNING_STATUSES = new Set(['pending', 'running']);

/** The one answer to "is this job still going". Read from a job that
 *  may be null (nothing started yet) so no caller has to spell the
 *  null case, and read by BOTH the poll cadence and every control that
 *  must be disabled mid-run -- those two disagreeing is precisely how
 *  a Re-analyze button becomes clickable during its own run. */
export function analysisIsRunning(job: SupportAnalysisResponse | null | undefined): boolean {
  return job != null && RUNNING_STATUSES.has(job.status);
}

/** How often a running job is polled. 750ms, not the 1.5s this used to
 *  be: the response now carries live progress and a growing assessment,
 *  so each poll is a frame of an animation rather than a "still not
 *  done?" check, and at 1.5s the streamed text arrives in visible
 *  lurches. Nothing is polled at all once the job is `done`/`failed`. */
const ANALYSIS_POLL_MS = 750;

export interface AnalysisJobHandle {
  /** The job to render, or null when nothing has been started for this
   *  target in this session. */
  job: SupportAnalysisResponse | null;
  /** Start a run. `force` skips the server's finished-analysis cache --
   *  what "Re-analyze" sends. */
  start: (opts?: { force?: boolean }) => void;
  /** The POST is in flight (the job does not exist yet). */
  starting: boolean;
  startError: unknown;
  /** The poll for this job failed. TERMINAL: nothing will poll this
   *  job again, so a caller that does not render this leaves an
   *  operator watching a status line that has stopped meaning
   *  anything. Both panes render it. */
  pollError: unknown;
  /** Any part of a run is in flight: the POST, or a job the server has
   *  not finished AND is still being polled. The one flag every control
   *  disables on -- which is why a dead poll must clear it. */
  busy: boolean;
}

/**
 * The whole client half of an analysis job: starting one, holding on
 * to what the POST said, and polling until it finishes.
 *
 * It lives beside the rendering, and is used by both panes, because
 * every part of it is a decision that must not fork:
 *
 *  - The POLL CADENCE. 750ms while `pending`/`running`, and `false`
 *    (not a slower number) once the job is `done` or `failed` -- a
 *    finished job is immutable, so a poll after it is a request that
 *    can only ever return what the caller already has.
 *
 *  - HOLDING THE POST RESPONSE. `POST /analyses` answers 200 with a
 *    finished, `cached: true` result when this target has been
 *    analysed before; `GET /analyses/{id}` returns that same job with
 *    `cached: false`, because from its point of view nothing came from
 *    a cache. So the POST response is the ONLY place the cache hit is
 *    knowable, and polling straight over it would silently downgrade a
 *    true answer to a lesser one. Hence: a job that arrives already
 *    finished is never polled -- which is also what makes a cache hit
 *    paint immediately instead of flashing a spinner.
 *
 * `started` is passed IN rather than held here so the caller decides
 * how long it survives: `TicketDetail` keeps it in local state (the
 * panel is remounted per ticket and a ticket's analysis should not
 * follow you to the next one), while `DiagnosePane` keeps a map of
 * them above the component that remounts, so a run that is still going
 * server-side is re-attached to rather than orphaned.
 */
export function useAnalysisJob({
  target,
  started,
  onStarted,
}: {
  target: string | { issue_id: string } | { ticket_id: string };
  started: SupportAnalysisResponse | null;
  onStarted: (resp: SupportAnalysisResponse) => void;
}): AnalysisJobHandle {
  const startMutation = useMutation({
    // `force` is left off the wire entirely when it is not set, so an
    // ordinary start sends exactly the body it always did.
    mutationFn: (force: boolean) =>
      force ? createSupportAnalysis(target, { force: true }) : createSupportAnalysis(target),
    onSuccess: onStarted,
  });

  const jobId = started?.id ?? null;
  const poll = useQuery({
    queryKey: ['support', 'analysis', jobId],
    queryFn: () => getSupportAnalysis(jobId as string),
    // Only a job that was still going when we got it is worth a GET;
    // see the `cached` note above for why a finished one must not be.
    enabled: jobId !== null && analysisIsRunning(started),
    refetchInterval: (query) =>
      analysisIsRunning(query.state.data) ? ANALYSIS_POLL_MS : false,
  });

  /**
   * A poll that ERRORS is the end of this job, not a gap in it.
   *
   * `enabled` is gated on the job the POST returned, so if the very
   * first `GET /analyses/{id}` 404s or fails there is never a
   * `poll.data` — the panel renders the POST's `status: 'pending'`
   * for ever, and because that reads as "still running" it also holds
   * `busy` down, disabling the one control that could recover it.
   * Re-analyze starts a NEW job, which is the only real recovery
   * available, so it must be reachable. The error is surfaced by both
   * panes and the run stops counting as in flight.
   *
   * A poll that fails LATER, having already returned a running job,
   * keeps its last reading on screen and keeps retrying (the cadence
   * reads the last data, not the error). Re-analyze is released in
   * that case too, and that is right: pressing it starts a fresh job,
   * which is never the wrong answer to "the console has stopped being
   * able to see this one".
   */
  const pollError = poll.isError ? poll.error : null;

  // WHICH OF THE TWO ANSWERS IS THE JOB.
  //
  // While the job is still going the POLL is authoritative: it is the
  // only one of the two that ever changes.
  //
  // Once it is NOT going, the POST response wins. `cached` and the
  // ORIGINAL `created_at_ms` are knowable only from the POST -- `GET
  // /analyses/{id}` reports the same job with `cached: false` -- so
  // preferring a poll result unconditionally silently downgraded a
  // cache hit that arrived while a stale poll for the SAME job id was
  // still in the query cache, losing both the "cached" marker and the
  // age it dates. A finished job is immutable, so nothing is lost in
  // the other direction.
  const job =
    (analysisIsRunning(started) ? (poll.data ?? started) : (started ?? poll.data)) ?? null;
  return {
    job,
    start: (opts) => startMutation.mutate(opts?.force === true),
    starting: startMutation.isPending,
    startError: startMutation.isError ? startMutation.error : null,
    pollError,
    busy: startMutation.isPending || (pollError == null && analysisIsRunning(job)),
  };
}
